import { useQuery, useQueryClient } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";

// Mirrors inrange_agents.operations.DashboardView on the BE.
// Project-centric: lifecycle tiles + a list of per-project
// mini-summaries (next step + risk chips). One round trip.

export type LifecycleStageId =
  | "predevelopment"
  | "construction"
  | "operational"
  | "decommissioned";

export interface LifecycleTile {
  id: LifecycleStageId;
  label: string;
  count: number;
  kwp_total: number;
}

export type RiskKind = "failed_extraction";

export interface ProjectRisk {
  kind: RiskKind;
  title: string;
  detail: string | null;
  age_days: number | null;
}

// "manager": the Manager's first open pick; "actions": the top of the specialists'
// Priority order (inrange_agents.operations.next_step_for).
export type NextStepSource = "manager" | "actions" | null;

export interface DriveSyncRecent {
  enabled: boolean;
  last_synced_at: string | null;
  added: number;
  changed: number;
  removed: number;
  status: string | null;
}

export interface ProjectSummary {
  project_id: string;
  project_name: string;
  address: string | null;
  // Counterparty org names, for the operations-page org filter (mirrors the
  // projects page). All four roles are treated as a flat org list (see
  // utils/projectOrgs.ts).
  landlord: string | null;
  tenant: string | null;
  primary_epc: string | null;
  lifecycle_stage: LifecycleStageId | "unknown";
  status: "draft" | "submitted" | "archived";
  target_system_size_kwp: number | null;
  next_step: string | null;
  next_step_source: NextStepSource;
  risks: ProjectRisk[];
  drive_sync_recent: DriveSyncRecent | null;
}

export interface OperationsDashboard {
  lifecycle: LifecycleTile[];
  projects: ProjectSummary[];
  drafts: { project_id: string; project_name: string }[];
  generated_at: string;
}

const QUERY_KEY = QK.operationsDashboard;

// Shared invalidator for the operations dashboard query, callable from any
// mutation's onSuccess (mirrors invalidateCost in useCostSummary). The
// dashboard renders per-project status - risk chips, next step,
// review/file badges - so a mutation that changes any of those
// must invalidate it here, or the page serves its 60s-stale cached copy until
// the staleTime expires (the "out of date for a few seconds" symptom).
export const invalidateOperationsDashboard = (
  qc: ReturnType<typeof useQueryClient>,
) => {
  qc.invalidateQueries({ queryKey: [QUERY_KEY] });
};

export const useOperationsDashboard = () => {
  const http = useAgentsHttp();
  return useQuery<OperationsDashboard>(
    [QUERY_KEY],
    async () => http<OperationsDashboard>("/operations/dashboard", {}),
    {
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  );
};

export const useInvalidateOperationsDashboard = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: [QUERY_KEY] });
};
